import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import HOC from '../../components/HOC/HOC';
import Pagination from '../../components/Pagination/Pagination';
import styles from '../../css/style.module.css';
import img from '../../assest/loading1.gif';
import { getApi } from '../../Repository/Api';
import endPoints from '../../Repository/apiConfig';
import { formatDateTimeP } from '../../utils/utils';
import { formatCurrency } from '../Wallet Management/staffWalletDateUtils';
import pageStyles from './StaffLedger.module.css';

const TYPE_OPTIONS = [
  { label: 'All Types', value: '' },
  { label: 'Credit', value: 'Credit' },
  { label: 'Debit / Withdrawal', value: 'Withdrawal' },
  { label: 'Other', value: 'Other' },
];

const StaffLedger = () => {
  const { id } = useParams();
  const [ledgerData, setLedgerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({
    fromDate: '',
    toDate: '',
    type: '',
  });
  const [pagination, setPagination] = useState({
    limit: 10,
    totalPages: 1,
    page: 1,
    hasPrevPage: false,
    hasNextPage: false,
  });

  const fetchData = useCallback(async () => {
    if (!id) return;

    await getApi(
      endPoints.getStaffWalletTransactions(
        id,
        pagination.page,
        pagination.limit,
        appliedFilters.fromDate,
        appliedFilters.toDate,
        appliedFilters.type
      ),
      {
        setResponse: setLedgerData,
        setLoading,
        errorMsg: 'Failed to fetch staff ledger!',
        onError: () => setLedgerData(null),
      }
    );
  }, [id, pagination.page, pagination.limit, appliedFilters]);

  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      totalPages: ledgerData?.data?.totalPages ?? 1,
      hasPrevPage: ledgerData?.data?.hasPrevPage ?? false,
      hasNextPage: ledgerData?.data?.hasNextPage ?? false,
    }));
  }, [ledgerData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApplyFilters = () => {
    setAppliedFilters({ fromDate, toDate, type: selectedType });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setFromDate('');
    setToDate('');
    setSelectedType('');
    setAppliedFilters({ fromDate: '', toDate: '', type: '' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const staff = ledgerData?.staff;
  const summary = ledgerData?.summary;
  const transactions = ledgerData?.data?.docs ?? [];
  const staffName =
    staff?.fullName || [staff?.firstName, staff?.lastName].filter(Boolean).join(' ') || '-';
  const hasActiveFilters = Boolean(
    appliedFilters.fromDate || appliedFilters.toDate || appliedFilters.type
  );

  return (
    <div className={`${styles.jobmanagementCotainer} ${pageStyles.page}`}>
      <div className={pageStyles.header}>
        <div className={pageStyles.headerLeft}>
          <h6>Staff Wallet Ledger</h6>
          <p>Transaction history and wallet summary</p>
          {staff && (
            <div className={pageStyles.staffMeta}>
              <span>
                <strong>Name:</strong> {staffName}
              </span>
              <span>
                <strong>Wallet ID:</strong> {staff?.Id || '-'}
              </span>
              <span>
                <strong>Email:</strong> {staff?.email || '-'}
              </span>
              <span>
                <strong>Phone:</strong> {staff?.phone || '-'}
              </span>
              <span>
                <strong>Wallet Balance:</strong> {formatCurrency(staff?.wallet)}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className={pageStyles.summaryGrid}>
        <div className={pageStyles.summaryCard}>
          <p className={pageStyles.summaryCardLabel}>Total Jobs Done</p>
          <p className={pageStyles.summaryCardValue}>{summary?.totalJobsDone ?? 0}</p>
        </div>
        <div className={pageStyles.summaryCard}>
          <p className={pageStyles.summaryCardLabel}>Total Earning</p>
          <p className={pageStyles.summaryCardValue}>
            {formatCurrency(summary?.totalEarning)}
          </p>
        </div>
        <div className={pageStyles.summaryCard}>
          <p className={pageStyles.summaryCardLabel}>Other Credits</p>
          <p className={pageStyles.summaryCardValue}>
            {formatCurrency(summary?.totalOtherCredits)}
          </p>
        </div>
        <div className={pageStyles.summaryCard}>
          <p className={pageStyles.summaryCardLabel}>Total Wallet Credits</p>
          <p className={pageStyles.summaryCardValue}>
            {formatCurrency(summary?.totalWalletCredits)}
          </p>
        </div>
        <div className={pageStyles.summaryCard}>
          <p className={pageStyles.summaryCardLabel}>Total Withdrawal</p>
          <p className={pageStyles.summaryCardValue}>
            {formatCurrency(summary?.totalWithdrawal)}
          </p>
        </div>
        <div className={pageStyles.summaryCard}>
          <p className={pageStyles.summaryCardLabel}>Current Wallet</p>
          <p className={pageStyles.summaryCardValue}>{formatCurrency(staff?.wallet)}</p>
        </div>
      </div>

      <div className={pageStyles.filterPanel}>
        <div className={pageStyles.filterField}>
          <label htmlFor="ledger-from-date">From Date</label>
          <input
            id="ledger-from-date"
            type="date"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
          />
        </div>
        <div className={pageStyles.filterField}>
          <label htmlFor="ledger-to-date">To Date</label>
          <input
            id="ledger-to-date"
            type="date"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            min={fromDate || undefined}
          />
        </div>
        <div className={pageStyles.filterField}>
          <label htmlFor="ledger-type">Type</label>
          <select
            id="ledger-type"
            value={selectedType}
            onChange={e => setSelectedType(e.target.value)}
          >
            {TYPE_OPTIONS.map(option => (
              <option key={option.value || 'all'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <button type="button" className={pageStyles.filterButton} onClick={handleApplyFilters}>
          Apply
        </button>
        <button
          type="button"
          className={pageStyles.clearButton}
          onClick={handleClearFilters}
          disabled={!hasActiveFilters && !fromDate && !toDate && !selectedType}
        >
          Clear
        </button>
      </div>

      <div className={pageStyles.tableSection}>
        <div className={pageStyles.tableSectionHeader}>
          <h6>Ledger Transactions</h6>
        </div>
        <div className={pageStyles.tableWrap}>
          <table className={pageStyles.table}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Site Name</th>
                <th>Type</th>
                <th>Category</th>
                <th>Description</th>
                <th>Created By</th>
                <th>Payment Mode</th>
                <th>Withdrawal</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className={pageStyles.loadingCell}>
                    <img src={img} alt="Loading..." width={80} height={80} />
                  </td>
                </tr>
              ) : transactions.length ? (
                transactions.map(item => (
                  <tr key={item._id}>
                    <td>{formatDateTimeP(item?.date) || '-'}</td>
                    <td>{item?.status || '-'}</td>
                    <td
                      className={
                        item?.type === 'Credit' ? pageStyles.creditAmount : pageStyles.debitAmount
                      }
                    >
                      {item?.type === 'Credit' ? '+' : '-'}
                      {formatCurrency(item?.amount)}
                    </td>
                    <td>{item?.siteName || '-'}</td>
                    <td>
                      <span
                        className={`${pageStyles.typeBadge} ${
                          item?.type === 'Credit' ? pageStyles.typeCredit : pageStyles.typeDebit
                        }`}
                      >
                        {item?.type || '-'}
                      </span>
                    </td>
                    <td>{item?.transactionCategory || '-'}</td>
                    <td>{item?.description || '-'}</td>
                    <td>{item?.createdBy || '-'}</td>
                    <td>{item?.paymentMode || '-'}</td>
                    <td>{item?.isWithdrawal ? 'Yes' : 'No'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className={pageStyles.emptyCell}>
                    No ledger transactions found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className={pageStyles.paginationWrap}>
          <Pagination
            limit={pagination.limit}
            totalPages={pagination.totalPages}
            page={pagination.page}
            hasPrevPage={pagination.hasPrevPage}
            hasNextPage={pagination.hasNextPage}
            onPageChange={newPage => setPagination({ ...pagination, page: newPage })}
          />
        </div>
      </div>
    </div>
  );
};

export default HOC(StaffLedger);
